import { useRef, useState } from "react";
import { Buffer } from "buffer";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// libraries
import moment from "moment";
// MUI
import { Box, Typography, Skeleton, Stack, Icon } from "@mui/material";
import IconAddPhoto from "@mui/icons-material/AddPhotoAlternate";
import IconErrorOutline from "@mui/icons-material/ErrorOutlineRounded";
// Hooks
// Components
import ImageStorePicture from "./ImageStorePicture";
// apis
import { updateStorePicture } from "@/Settings/api/AdministrationTools/mutations";
import { retrieveLocationWithStorePicture } from "@Common/api/location/queries";
import { getStorePresignedUrl } from "@/Settings/api/AdministrationTools/queries";
// Helpers
import { getTenantParameters } from "@/Common/helpers/Tenant";
import { apiInstance } from "@/services/httpAdapter";
import { errorLogger } from "@/Common/helpers/logger";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

export default function StorePicture(props) {
	const location = useSelector((state) => state.location.location);
	const locationId = location?.locationId;
	const queryClient = useQueryClient();

	const [errors, setErrors] = useState([]);
	const fileInputRef = useRef(null);

	const [numStorePics, setNumStorePics] = useState(0);

	const uploadAllFilesToS3 = async (selectedFiles, operation, modifyIndex = -1) => {
		try {
			await Promise.all(selectedFiles.map((file, index) => uploadFileToS3Helper(file, operation, modifyIndex, index)));
		} catch (error) {
			errorLogger(error);
		}
	};

	const convertImageToBase64 = async (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onload = () => {
				resolve(reader?.result);
			};
			reader.onerror = (error) => {
				reject(error);
			};
			reader.readAsDataURL(file);
		});
	};

	const uploadFileToS3Helper = async (file, operation, modifyIndex, index) => {
		convertImageToBase64(file).then((result) => {
			uploadFileToS3(result, operation, modifyIndex, index, file?.type);
		});
	};

	const uploadFileToS3 = async (file, operation, modifyIndex, index, originalFileType) => {
		try {
			const options = {
				headers: {
					"Content-Type": "image/*",
				},
			};

			// build file identifier with timestamp, extension, and counter (temporary identifier)
			const timestamp = moment().format(DATE_FORMAT.isoCompact);
			const fileExtension = originalFileType.split("/")[1];
			const identifier = `${index}-${timestamp}.${fileExtension}`;

			const tenantId = getTenantParameters(location?.locationId)?.tenant;
			if (tenantId) {
				// getting presignedUrl to save image.
				const response = await getStorePresignedUrl(location.locationId, identifier);
				const buf = Buffer.from(file.replace(/^data:image\/\w+;base64,/, ""), "base64");
				if (response?.presignedUrl) {
					const uploadResponse = await apiInstance.put(response.presignedUrl, buf, options);
					if (uploadResponse.status === 200) {
						// build the input for the update
						let updateInput = { pictureURL: `store-picture-${identifier}` };
						let response = await updateStorePicture(location.locationId, operation, updateInput, modifyIndex);
						setErrors([]);
						queryClient.invalidateQueries({ queryKey: ["storePicturesList"] });
					}
				}
			}
		} catch (error) {
			errorLogger(error);
		}

		return false;
	};

	const { data: srcImageList, isLoading } = useQuery({
		queryKey: ["storePicturesList"],
		queryFn: async () => {
			const loc = await retrieveLocationWithStorePicture(locationId);
			const data = loc?.storePictures || [];
			setNumStorePics(data?.length || 0);
			return data;
		},

		enabled: !!locationId,
		staleTime: Infinity,
	});

	const handleAddImage = (event) => {
		fileInputRef.current.click();
	};

	const validateFiles = (selectedFiles = []) => {
		let errors = [];

		// validate fileCount
		let totalFiles = (selectedFiles?.length || 0) + numStorePics;
		let isFileCountValid = totalFiles <= 5;

		if (!isFileCountValid) errors.push("Number of total pictures cannot exceed 5.");

		let isFileSizeValid = true;
		let isFileTypeValid = true;

		// validate file size and file type
		selectedFiles.forEach((file) => {
			isFileSizeValid = isFileSizeValid && file?.size <= 5000000;
			isFileTypeValid = isFileTypeValid && ["image/png", "image/jpeg"].includes(file.type);
		});

		if (!isFileSizeValid) errors.push("File size cannot exceed 5MB.");
		if (!isFileTypeValid) errors.push("File format is not supported. (Image must be PNG or JPEG)");

		return errors || [];
	};

	const handleFileUpload = async (event) => {
		const newFiles = event.target.files || [];
		const newFilesArray = Array.from(newFiles);

		let errors = validateFiles(newFilesArray);
		if (!errors?.length) {
			await uploadAllFilesToS3(newFilesArray, "ADD");
		} else {
			setErrors(errors);
		}
	};

	return (
		<Stack sx={{ paddingRight: 5 }}>
			<Typography className="regular-pt-sans-pro-narrow-label" sx={{ display: "inline", paddingBottom: "10px" }}>
				Upload up to 5 pictures of your dealership. Images must not exceed 5MB in size.
			</Typography>
			<Stack direction="row" spacing={2}>
				{isLoading ? (
					<Skeleton variant="rectangular" width={750} height={150} sx={{ borderRadius: "2px" }}></Skeleton>
				) : (
					<>
						{srcImageList?.map((srcImage, index) => {
							return (
								<Box
									key={index}
									mb={1}
									sx={{
										width: 150,
										height: 150,
										borderRadius: 1,
										overflow: "hidden",
									}}
								>
									<ImageStorePicture
										src={srcImage.pictureURL}
										width={150}
										height={150}
										pictureIndex={index}
										validator={validateFiles}
										uploader={uploadAllFilesToS3}
										numStorePics={numStorePics}
										locationId={locationId}
										setErrors={setErrors}
									/>
								</Box>
							);
						})}
						{srcImageList?.length < 5 && (
							<Box mb={1} className={"add-img-box"} onClick={handleAddImage}>
								<input type="file" style={{ display: "none" }} ref={fileInputRef} multiple onChange={handleFileUpload} />
								<Stack sx={{ justifyContent: "center", alignItems: "center" }}>
									<IconAddPhoto className="add-img-box-icons"></IconAddPhoto>
									<Typography className="add-img-box-text">Add an Image</Typography>
								</Stack>
							</Box>
						)}
					</>
				)}
			</Stack>
			{errors?.length ? (
				errors.map((error, index) => {
					return (
						<Stack key={index} direction={"row"} spacing={1} sx={{ alignItems: "center" }}>
							<IconErrorOutline className="store-picture-alert-warning-icon"></IconErrorOutline>
							<Typography className="store-picture-alert-warning">{error}</Typography>
						</Stack>
					);
				})
			) : (
				<></>
			)}
		</Stack>
	);
}
